import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasLocalKey, registerBiometricKey, requestBiometricGesture } from "@/lib/crypto";
import { pushPermission, enablePush as enablePushSub } from "@/lib/push";
import { ShieldCheck, Check, Smartphone, Eye, Fingerprint, BadgeCheck, Link2, Trash2, Bell } from "lucide-react";
import { TIER_LABEL, tierBadgeClass } from "@/lib/tiers";

function Toggle({ on, onChange, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={() => onChange(!on)}
      className={`relative w-12 h-7 rounded-full transition-colors ${on ? "bg-[#1B4D3E]" : "bg-[#DEDBD3]"}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
    </button>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [key, setKey] = useState(null);
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [localReady, setLocalReady] = useState(false);

  // license
  const [lic, setLic] = useState({ state: "Nevada", license_number: "", legal_name: "" });
  const [licStatus, setLicStatus] = useState(null);
  // shares
  const [shares, setShares] = useState([]);
  const [pushPerm, setPushPerm] = useState(pushPermission());

  const tier = profile?.verification_tier || user?.identity_verification?.verification_tier || "unverified";
  const verifiedRef = user?.identity_verification?.verification_reference || "";
  const verifiedPhone = verifiedRef.startsWith("phone:") ? verifiedRef.replace("phone:", "") : null;

  const load = async () => {
    const [k, p, l, s] = await Promise.all([
      api.get("/keys/me"), api.get("/profile/me"),
      api.get("/identity/license/me"), api.get("/share/list"),
    ]);
    setKey(k.data); setProfile(p.data);
    setLicStatus(l.data[0]?.status || null);
    setShares(s.data);
    setLocalReady(await hasLocalKey(user.email));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setupDevice = async () => {
    setBusy(true);
    try {
      await requestBiometricGesture(user.email);
      const pub = await registerBiometricKey(user.email);
      const r = await api.post("/keys/register", { public_key_der: pub });
      toast.success("This device is now set up to seal daps.");
      setLocalReady(true);
      setKey({ has_key: true, key_id: r.data.key_id });
    } catch (e) { toast.error("Could not set up biometric key."); }
    finally { setBusy(false); }
  };

  const submitLicense = async () => {
    try {
      await api.post("/identity/license", lic);
      toast.success("License submitted for review.");
      setLicStatus("pending_review");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const revokeShare = async (token) => {
    await api.post(`/share/${token}/revoke`);
    toast("Share link revoked.");
    load();
  };

  const toggleVisibility = async (val) => {
    try {
      await api.post("/profile/visibility", { opt_in: val });
      setProfile((p) => ({ ...p, directory_opt_in: val }));
    } catch (e) { toast.error("Could not update visibility."); }
  };

  const enablePush = async () => {
    const res = await enablePushSub();
    setPushPerm(pushPermission());
    if (res.status === "granted")
      toast.success("Push notifications enabled. You'll be alerted on invites and seals — even when the app is closed.");
    else if (res.status === "denied")
      toast.error("Notifications are blocked. Enable them in your browser settings.");
    else if (res.status === "needs-install")
      toast.info("On iPhone, add Dap Ledger to your Home Screen first (Share → Add to Home Screen), then open it and enable notifications.");
    else if (res.status === "server-disabled")
      toast.error("Push is not configured on the server yet.");
    else if (res.status === "unsupported")
      toast.error("This browser doesn't support push notifications.");
    else toast.error("Could not enable notifications. Please try again.");
  };

  return (
    <div data-testid="settings-page" className="max-w-2xl mx-auto space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <p className="overline text-[#E07A5F]">Account</p>
          <h1 className="font-serif-dl text-4xl sm:text-5xl tracking-tight mt-2">Settings</h1>
        </div>
        <span className={`overline px-2.5 py-1 rounded border inline-flex items-center gap-1 ${tierBadgeClass(tier)}`}>
          {tier === "license" && <BadgeCheck size={12} />}{TIER_LABEL[tier]}
        </span>
      </div>

      {/* Identity verification */}
      <section className="bg-white border border-[#DEDBD3] rounded-lg p-6 sm:p-8" data-testid="identity-section">
        <div className="flex items-center gap-2 text-[#1B4D3E]">
          <ShieldCheck size={18} />
          <h2 className="font-serif-dl text-2xl tracking-tight">Verify your identity</h2>
        </div>
        <p className="text-[#5C5A54] mt-2 text-sm">
          Your word should be worth something. Verify to seal daps. One verified
          identity per person.
        </p>

        {/* Identity — verified via Google email */}
        <div className="mt-6 rounded-lg bg-[#F9F8F6] border border-[#DEDBD3] p-4" data-testid="identity-block">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={16} className="text-[#1B4D3E]" />
            <p className="text-sm font-semibold">
              Identity {tier !== "unverified" && <span className="text-[#2E6B4F]">✓ verified</span>}
            </p>
          </div>
          <p className="text-sm text-[#5C5A54]" data-testid="verified-identity">
            Verified via your Google email <span className="font-mono-dl">{user?.email}</span>.
            {verifiedPhone && <> A phone number (<span className="font-mono-dl">{verifiedPhone}</span>) is also on file.</>}
          </p>
          <p className="text-xs text-[#8A877F] mt-2">
            This confirms your identity so you can seal daps. One verified identity per person.
          </p>
        </div>

        {/* License tier */}
        {tier !== "unverified" && (
          <div className="mt-4 rounded-lg bg-[#F9F8F6] border border-[#DEDBD3] p-4" data-testid="license-block">
            <div className="flex items-center gap-2 mb-3">
              <BadgeCheck size={16} className="text-[#1B4D3E]" />
              <p className="text-sm font-semibold">
                Verify professional license (optional)
                {tier === "license" && <span className="text-[#2E6B4F]"> ✓ verified</span>}
              </p>
            </div>
            {tier !== "license" && (
              licStatus === "pending_review" ? (
                <p className="text-sm text-[#B07B2E]" data-testid="license-pending">Your license is pending review.</p>
              ) : (
                <div className="space-y-2">
                  <select
                    data-testid="license-state" value={lic.state}
                    onChange={(e) => setLic({ ...lic, state: e.target.value })}
                    className="w-full bg-white border border-[#DEDBD3] rounded-md px-3 py-2 text-sm"
                  >
                    <option value="Nevada">Nevada</option>
                  </select>
                  <input
                    data-testid="license-number" value={lic.license_number}
                    onChange={(e) => setLic({ ...lic, license_number: e.target.value })}
                    placeholder="License number"
                    className="w-full bg-white border border-[#DEDBD3] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B4D3E]"
                  />
                  <input
                    data-testid="license-name" value={lic.legal_name}
                    onChange={(e) => setLic({ ...lic, legal_name: e.target.value })}
                    placeholder="Full legal name"
                    className="w-full bg-white border border-[#DEDBD3] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B4D3E]"
                  />
                  <button
                    data-testid="license-submit-btn"
                    disabled={!lic.license_number || !lic.legal_name}
                    onClick={submitLicense}
                    className="w-full py-2.5 bg-[#1B4D3E] text-white rounded-md text-sm hover:bg-[#143A2F] disabled:opacity-50"
                  >
                    Submit for verification
                  </button>
                </div>
              )
            )}
          </div>
        )}
      </section>

      {/* Device / biometric key */}
      <section className="bg-white border border-[#DEDBD3] rounded-lg p-6 sm:p-8">
        <div className="flex items-center gap-2 text-[#1B4D3E]">
          <Smartphone size={18} />
          <h2 className="font-serif-dl text-2xl tracking-tight">This Device</h2>
        </div>
        <p className="text-[#5C5A54] mt-2 text-sm">
          Your biometric key lives on this device. On a new device, set it up again —
          past daps stay valid, and your standing follows your identity, not the device.
        </p>
        <div className="mt-6 flex items-center justify-between rounded-lg bg-[#F9F8F6] border border-[#DEDBD3] p-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${localReady ? "bg-[#E6F0EB] text-[#2E6B4F]" : "bg-[#F0EBE1] text-[#A3A095]"}`}>
              {localReady ? <Check size={18} /> : <Fingerprint size={18} />}
            </div>
            <div>
              <p className="text-sm font-medium" data-testid="device-key-status">
                {localReady ? "This device is set up" : "This device needs setup"}
              </p>
              {key?.key_id && <p className="text-xs text-[#8A877F] font-mono-dl">key · {key.key_id}</p>}
            </div>
          </div>
          <button data-testid="setup-device-btn" disabled={busy} onClick={setupDevice}
            className="bg-[#E07A5F] hover:bg-[#C96A51] disabled:opacity-60 text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2">
            <Fingerprint size={16} />{localReady ? "Re-register" : "Set up device"}
          </button>
        </div>
      </section>

      {/* Shared links */}
      <section className="bg-white border border-[#DEDBD3] rounded-lg p-6 sm:p-8" data-testid="shared-links-section">
        <div className="flex items-center gap-2 text-[#1B4D3E]">
          <Link2 size={18} />
          <h2 className="font-serif-dl text-2xl tracking-tight">Shared links</h2>
        </div>
        <p className="text-[#5C5A54] mt-2 text-sm">Standing you've chosen to share. Revoke anytime.</p>
        <div className="mt-4 space-y-2">
          {shares.length === 0 ? (
            <p className="text-sm text-[#A3A095]">No active share links.</p>
          ) : shares.map((s) => (
            <div key={s.token} data-testid={`share-row-${s.token}`} className="flex items-center justify-between rounded-md bg-[#F9F8F6] border border-[#DEDBD3] px-3 py-2">
              <div className="min-w-0">
                <p className="text-xs font-mono-dl truncate">/s/{s.token}</p>
                <p className="text-[10px] text-[#A3A095]">{s.active ? "Active" : "Expired / revoked"} · expires {new Date(s.expires_at).toLocaleString()}</p>
              </div>
              {s.active && (
                <button data-testid={`revoke-${s.token}`} onClick={() => revokeShare(s.token)} className="text-[#9C3A3A] hover:text-[#7a2d2d]">
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Push notifications */}
      <section className="bg-white border border-[#DEDBD3] rounded-lg p-6 sm:p-8" data-testid="push-section">
        <div className="flex items-center gap-2 text-[#1B4D3E]">
          <Bell size={18} />
          <h2 className="font-serif-dl text-2xl tracking-tight">Push notifications</h2>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div className="pr-6">
            <p className="text-sm font-medium">Get alerted the moment a dap moves</p>
            <p className="text-xs text-[#5C5A54] mt-1">
              We'll push you when someone invites you to a dap and when a dap is sealed —
              live, even when this tab is in the background.
            </p>
          </div>
          {pushPerm === "granted" ? (
            <span data-testid="push-status" className="overline px-2.5 py-1 rounded border bg-[#E6F0EB] text-[#2E6B4F] border-[#2E6B4F]/30 inline-flex items-center gap-1">
              <Check size={12} /> On
            </span>
          ) : pushPerm === "denied" ? (
            <span data-testid="push-status" className="overline px-2.5 py-1 rounded border bg-[#F5E6E6] text-[#9C3A3A] border-[#9C3A3A]/20">Blocked</span>
          ) : pushPerm === "unsupported" ? (
            <span data-testid="push-status" className="overline px-2.5 py-1 rounded border bg-[#F0EBE1] text-[#8A877F] border-[#DEDBD3]">Unsupported</span>
          ) : (
            <button
              data-testid="enable-push-btn"
              onClick={enablePush}
              className="bg-[#1B4D3E] text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#143A2F]"
            >
              Enable
            </button>
          )}
        </div>
      </section>

      {/* Community visibility */}
      <section className="bg-white border border-[#DEDBD3] rounded-lg p-6 sm:p-8">
        <div className="flex items-center gap-2 text-[#1B4D3E]">
          <Eye size={18} />
          <h2 className="font-serif-dl text-2xl tracking-tight">Community Directory</h2>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div className="pr-6">
            <p className="text-sm font-medium">Appear in the community directory</p>
            <p className="text-xs text-[#5C5A54] mt-1">
              Private by default. Opt in to let members see your honor standing — a
              reputation of kept promises, never a public credit score.
            </p>
          </div>
          <Toggle testid="visibility-toggle" on={!!profile?.directory_opt_in} onChange={toggleVisibility} />
        </div>
      </section>

      {user?.is_admin && (
        <Link to="/admin" data-testid="admin-link" className="block text-center text-sm text-[#1B4D3E] hover:underline">
          Go to Admin →
        </Link>
      )}
    </div>
  );
}
